import { Anchor, Badge, Button, Group } from '@mantine/core';
import {
  IconBell,
  IconBrandGoogleMaps,
  IconBrandWaze,
  IconCalendarRepeat,
  IconCalendarTime,
  IconCategory,
  IconCategoryPlus,
  IconFileDescription,
  IconBed,
  IconHome,
  IconLock,
  IconMapPin,
  IconUsers,
  IconVideo,
} from '@tabler/icons-react';
import dayjs from 'dayjs';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { KeyValueLine } from 'src/Components/Shared/KeyValueLine';
import { humanizeRRule } from 'src/Services/Shared/humanizeRRule';
import EventTypeColors from 'src/Utils/eventTypeColors';

import styles from './EventDetails.module.scss';
import { Props } from './EventDetails.types';
import { EventSmartLinks } from './EventSmartLinks';

const EventDetails = ({
  event,
  occurrenceStartTime,
  rightTitleSection,
  bottomSection,
  isLoading,
}: Props) => {
  const { t } = useTranslation();

  const displayDates = useMemo(() => {
    if (!event) return { startTime: undefined, endTime: undefined };

    if (occurrenceStartTime && event.isRecurring) {
      const occurrenceStart = occurrenceStartTime;
      const duration = event.endTime?.diff(event.startTime);
      const occurrenceEnd = duration
        ? occurrenceStart.add(duration, 'millisecond')
        : occurrenceStart;

      return {
        startTime: occurrenceStart,
        endTime: occurrenceEnd,
      };
    }

    return {
      startTime: event.startTime,
      endTime: event.endTime,
    };
  }, [event, occurrenceStartTime]);

  const address = event?.address ?? undefined;
  // Falls back on the legacy single household for responses from an API predating `households`.
  const households = event?.households ?? (event?.household ? [event.household] : []);
  const mapsUrl = address
    ? `https://www.google.com/maps/search/?api=1&query=${address.latitude},${address.longitude}`
    : undefined;
  const wazeUrl = address
    ? `https://waze.com/ul?ll=${address.latitude},${address.longitude}&navigate=yes`
    : undefined;

  const videoConferenceUrl = event?.videoConferenceUrl;

  const reminderText = useMemo(() => {
    if (event?.reminderDurationSeconds === 0) {
      return t('createEventForm.reminder.atTimeOfEvent', "Au début de l'événement");
    }
    if (event?.reminderDurationSeconds) {
      const humanizedDuration = dayjs.duration(event.reminderDurationSeconds, 'seconds').humanize();
      return `${humanizedDuration} ${t('event.details.before', 'avant')}`;
    }
    return t('createEventForm.noReminder', 'Aucun');
  }, [event?.reminderDurationSeconds, t]);

  return (
    <div className={styles.container}>
      <div className={styles.topLine}>
        <div className={styles.title}>{event?.name}</div>
        {rightTitleSection}
      </div>
      <KeyValueLine
        keyIcon={<IconCategory className={styles.icon} size={16} />}
        propertyKey={t('event.details.type', 'Type')}
        value={
          event?.type && (
            <Badge
              style={{ color: EventTypeColors[event.type]?.fgColor }}
              color={EventTypeColors[event.type]?.bgColor}
            >
              {t(`createEventForm.eventTypes.${event.type}`)}
            </Badge>
          )
        }
        loading={isLoading}
      />
      <KeyValueLine
        keyIcon={<IconCalendarTime className={styles.icon} size={16} />}
        propertyKey={t('event.details.startDate', 'Date de début')}
        value={displayDates.startTime?.format('LLLL')}
        loading={isLoading}
      />
      <KeyValueLine
        keyIcon={<IconCalendarTime className={styles.icon} size={16} />}
        propertyKey={t('event.details.endDate', 'Date de fin')}
        value={displayDates.endTime?.format('LLLL')}
        loading={isLoading}
      />
      <KeyValueLine
        keyIcon={<IconBell className={styles.icon} size={16} />}
        propertyKey={t('event.details.reminder', 'Rappel')}
        value={reminderText}
        loading={isLoading}
        className={
          event?.reminderDurationSeconds === undefined || event?.reminderDurationSeconds === null
            ? styles.none
            : undefined
        }
      />
      {households.length > 0 && (
        <KeyValueLine
          keyIcon={<IconHome className={styles.icon} size={16} />}
          propertyKey={t('event.details.households', {
            count: households.length,
            defaultValue_one: 'Foyer',
            defaultValue_other: 'Foyers',
          })}
          value={
            <Group gap="xs" wrap="wrap">
              {households.map(household => (
                <Group key={household.id} gap={6} wrap="nowrap">
                  <span
                    className={styles.householdDot}
                    style={{ backgroundColor: household.color }}
                  />
                  {household.name}
                </Group>
              ))}
            </Group>
          }
          loading={isLoading}
        />
      )}
      {event?.rooms && Array.isArray(event.rooms) && event.rooms.length > 0 && (
        <KeyValueLine
          keyIcon={<IconBed className={styles.icon} size={16} />}
          propertyKey={t('event.details.rooms', 'Chambres')}
          value={event.rooms
            .map(room => [room.name, room.building?.name].filter(Boolean).join(' · '))
            .join(', ')}
          loading={isLoading}
        />
      )}
      <KeyValueLine
        keyIcon={<IconUsers className={styles.icon} size={16} />}
        propertyKey={t('event.details.participants', 'Participants')}
        value={
          !event?.participants || event.participants.length === 0
            ? t('event.details.noParticipants', 'Aucun')
            : event?.participants.map(p => `${p.firstName} ${p.lastName}`).join(', ')
        }
        loading={isLoading}
        className={
          !event?.participants || event.participants.length === 0 ? styles.none : undefined
        }
      />
      <KeyValueLine
        keyIcon={<IconCalendarRepeat className={styles.icon} size={16} />}
        propertyKey={t('event.details.recurrence', 'Récurrence')}
        value={
          event?.isRecurring && event?.recurrence
            ? humanizeRRule(event.recurrence)
            : t('event.details.none', 'Aucune')
        }
        className={event?.isRecurring ? undefined : styles.none}
        loading={isLoading}
      />
      <KeyValueLine
        keyIcon={<IconFileDescription className={styles.icon} size={16} />}
        propertyKey={t('event.details.description', 'Motif')}
        value={event?.description}
        loading={isLoading}
      />
      <EventSmartLinks description={event?.description} />
      {address && (
        <>
          <KeyValueLine
            keyIcon={<IconMapPin className={styles.icon} size={16} />}
            propertyKey={t('event.details.address', 'Adresse')}
            value={address.formattedAddress}
            loading={isLoading}
          />
          <Group gap="xs">
            <Button
              component="a"
              href={mapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              variant="light"
              leftSection={<IconBrandGoogleMaps size={16} />}
            >
              {t('event.details.openInMaps', 'Google Maps')}
            </Button>
            <Button
              component="a"
              href={wazeUrl}
              target="_blank"
              rel="noopener noreferrer"
              variant="light"
              leftSection={<IconBrandWaze size={16} />}
            >
              {t('event.details.openInWaze', 'Waze')}
            </Button>
          </Group>
        </>
      )}
      {videoConferenceUrl && (
        <KeyValueLine
          keyIcon={<IconVideo className={styles.icon} size={16} />}
          propertyKey={t('event.details.videoConference', 'Visioconférence')}
          value={
            <Anchor href={videoConferenceUrl} target="_blank" rel="noopener noreferrer" fz={13}>
              {videoConferenceUrl}
            </Anchor>
          }
          loading={isLoading}
        />
      )}
      <KeyValueLine
        keyIcon={<IconLock className={styles.icon} size={16} />}
        propertyKey={t('event.details.exclusive', 'Occupation exclusive')}
        value={
          event?.isExclusiveOccupation
            ? t('event.details.yes', 'oui')
            : t('event.details.no', 'non')
        }
        loading={isLoading}
      />
      <KeyValueLine
        keyIcon={<IconCategoryPlus className={styles.icon} size={16} />}
        propertyKey={t('event.details.createdAt', 'Date de création')}
        value={event?.createdAt?.format('LLLL')}
        loading={isLoading}
      />
      <div className={styles.border} />
      {bottomSection}
    </div>
  );
};

export default EventDetails;
